/**
 * דף גלריה — Feedback1.md סעיף 7 (20.08.2026): מיזוג "אודות" הקיים + גלריית
 * תמונות. מחליף את `AboutPage.tsx`, שנמחק (התוכן המילולי עבר לכאן במלואו,
 * ⛔ לא צומצם — PRD 14.2 עדיין קובע "לא לצמצם לפי הקטלוג ההתחלתי").
 *
 * ⭐ 7.2 — מקור תמונות הגלריה: `GALLERY_IMAGES` (`content/galleryImages.ts`),
 *    9 קבצים סטטיים שנגזרו מ-`Backround images/` — ⛔ לא `New Media/` (זו
 *    נשארת תמונות קטלוג בלבד).
 * ⭐ 7.2 — Lightbox קיים (`components/Lightbox.tsx`) לשימוש חוזר, אותו דפוס
 *    בדיוק כמו `ProductPage.tsx`.
 * ⭐ 7.6 — `alt` תיאורי לכל תמונה (ב-`galleryImages.ts`), `loading="lazy"`,
 *    `onError` מוציא תמונה שנכשלה מהרשת בלי לשבור את הפריסה (אותו דפוס כמו
 *    `ProductCard.tsx`/`HeroShowcase.tsx`).
 */
import { useState } from 'react';
import { GALLERY_IMAGES } from '../content/galleryImages.js';
import { FULL_SCOPE_ITEMS } from '../content/businessInfo.js';
import { TintedSection } from '../components/TintedSection.js';
import { Lightbox } from '../components/Lightbox.js';

export function GalleryPage() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(new Set());
  const visibleImages = GALLERY_IMAGES.filter((img) => !failedUrls.has(img.url));

  return (
    <>
      {/* ⭐ 16.08.2026 - `TintedSection`, עקבי עם שאר הדפים הראשיים. תוכן
          "אודות" הקיים, ⛔ ללא שינוי מהותי - רק הועבר מ-`AboutPage.tsx`. */}
      <TintedSection>
        <div className="mx-auto max-w-[65ch] px-6 py-14">
          <div
            className="bg-canvas border-line rounded-lg border p-6 sm:p-10"
            style={{ boxShadow: 'var(--shadow-card)' }}
          >
            <h1 className="text-3xl font-bold">גלריה ואודות</h1>

            <p className="mt-6 text-lg leading-relaxed">
              נועם ייבוא ושיווק היא יבואנית ויצרנית פתרונות למערכות מיזוג אוויר. אנחנו מייבאים לוחות
              PIR ופנול (Phenolic), ומייצרים מהם תעלות, קופסאות ניפוח, מרכזיות, מתאמים והסתעפויות -
              לפי שרטוט ומידה שמותאמים לכל פרויקט. במקביל אנחנו משווקים גם אביזרים משלימים למערכות
              מיזוג, כמו תריסי רונה, שטוצרים וגמישים.
            </p>

            <section className="mt-10" aria-labelledby="materials-heading">
              <h2 id="materials-heading" className="text-xl font-bold">
                PIR מול Phenolic
              </h2>
              <p className="text-muted mt-3 leading-relaxed">
                אנחנו עובדים עם שני סוגי הלוחות המבודדים המובילים בענף - PIR ופנול (Phenolic) -
                ובוחרים את החומר המתאים לכל פרויקט לפי הדרישות שלו. הבידוד מובנה בלוח עצמו, מה שחוסך
                שכבת בידוד נפרדת ומקל על ההתקנה בשטח.
              </p>
            </section>

            <section className="mt-10" aria-labelledby="scope-heading">
              <h2 id="scope-heading" className="text-xl font-bold">
                תחום העיסוק המלא
              </h2>
              <p className="text-muted mt-3">
                מעבר למוצרים שמופיעים כרגע בקטלוג, אנחנו מייצרים ומספקים גם:
              </p>
              <ul className="mt-4 grid list-inside list-disc gap-2 sm:grid-cols-2">
                {FULL_SCOPE_ITEMS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="text-muted mt-4 text-sm">
                לא מצאתם את מה שאתם מחפשים בקטלוג? שלחו לנו שרטוט או תיאור, ונחזור אליכם עם הצעה
                מותאמת.
              </p>
            </section>

            <section className="mt-10" aria-labelledby="future-heading">
              <h2 id="future-heading" className="text-xl font-bold">
                לאן אנחנו הולכים
              </h2>
              <p className="text-muted mt-3 leading-relaxed">
                מעבר למיזוג אוויר, אנחנו שואפים להרחיב את הפעילות שלנו לענפים נוספים שבהם לוחות
                מבודדים ופתרונות בהתאמה אישית רלוונטיים.
              </p>
            </section>
          </div>
        </div>
      </TintedSection>

      {/* ⭐ גלריית התמונות עצמה - רוחב מלא (⛔ לא מוגבל ל-65ch כמו כרטיס
          הטקסט), עקבי עם רוחב רשת הקטלוג (`CatalogPage.tsx`). */}
      <section className="mx-auto max-w-6xl px-6 py-14" aria-labelledby="gallery-heading">
        <h2 id="gallery-heading" className="text-2xl font-bold">
          מהמפעל שלנו
        </h2>
        <p className="text-muted mt-2 max-w-2xl">
          מרכזיות למזגן אוויר בייצור עצמי, בהתאמה אישית לכל דגם ומידה - חלק מתחום העיסוק המלא שמעבר
          לקטלוג.
        </p>

        {visibleImages.length > 0 ? (
          <ul className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {visibleImages.map((image, index) => (
              <li key={image.url} className="list-none">
                <button
                  type="button"
                  onClick={() => setLightboxIndex(index)}
                  aria-label={`הגדלת תמונה: ${image.alt}`}
                  className="product-card block w-full overflow-hidden rounded-lg border border-line bg-canvas"
                  style={{ boxShadow: 'var(--shadow-card)' }}
                >
                  <img
                    src={image.url}
                    alt=""
                    loading="lazy"
                    width={468}
                    height={374}
                    onError={() =>
                      setFailedUrls((prev) =>
                        prev.has(image.url) ? prev : new Set(prev).add(image.url),
                      )
                    }
                    className="aspect-[4/3] w-full object-cover"
                  />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted mt-8">התמונות אינן זמינות כרגע.</p>
        )}
      </section>

      {lightboxIndex !== null && visibleImages.length > 0 && (
        <Lightbox
          images={visibleImages}
          index={lightboxIndex}
          onNavigate={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
